import { FIELD_DEFINITIONS, REQUIRED_FIELD_KEYS, matchFieldAlias } from './fields';
import type { FieldKey, FieldMapping, HeaderAliasMatch, HeaderRowCandidate } from './types';

export const HEADER_SCAN_ROW_LIMIT = 30;
export const HEADER_CONFIRMATION_CONFIDENCE = 0.65;

export function scoreHeaderRow(row: readonly unknown[], rowNumber: number): HeaderRowCandidate {
  const byField = new Map<FieldKey, HeaderAliasMatch>();

  row.forEach((value, index) => {
    const aliasMatch = matchFieldAlias(value);
    if (aliasMatch === null) return;
    const match: HeaderAliasMatch = {
      field: aliasMatch.field,
      column: index + 1,
      header: aliasMatch.header,
      alias: aliasMatch.alias,
      confidence: aliasMatch.confidence,
    };
    const previous = byField.get(match.field);
    if (previous === undefined || match.confidence > previous.confidence) {
      byField.set(match.field, match);
    }
  });

  const matches = [...byField.values()].sort((left, right) => left.column - right.column);
  const requiredMatches = matches.filter(({ field }) => REQUIRED_FIELD_KEYS.includes(field)).length;
  const aliasQuality = matches.reduce((sum, match) => sum + match.confidence, 0);
  const score = aliasQuality * 10 + requiredMatches * 2 + (matches.length >= 5 ? 5 : 0);
  const fieldCoverage = matches.length / FIELD_DEFINITIONS.length;
  const requiredCoverage =
    REQUIRED_FIELD_KEYS.length === 0 ? 1 : requiredMatches / REQUIRED_FIELD_KEYS.length;
  const confidence = Math.min(1, fieldCoverage * 0.65 + requiredCoverage * 0.35);

  return { rowNumber, score, confidence, matches };
}

export function detectHeaderRow(
  rows: readonly (readonly unknown[])[],
  maximumRows = HEADER_SCAN_ROW_LIMIT,
): HeaderRowCandidate | null {
  const scanCount = Math.min(Math.max(0, maximumRows), HEADER_SCAN_ROW_LIMIT, rows.length);
  let best: HeaderRowCandidate | null = null;
  for (let index = 0; index < scanCount; index += 1) {
    const row = rows[index];
    if (row === undefined) continue;
    const candidate = scoreHeaderRow(row, index + 1);
    if (
      candidate.matches.length > 0 &&
      (best === null ||
        candidate.score > best.score ||
        (candidate.score === best.score && candidate.rowNumber < best.rowNumber))
    ) {
      best = candidate;
    }
  }
  return best;
}

export function mappingFromHeaderCandidate(candidate: HeaderRowCandidate): FieldMapping {
  return Object.fromEntries(candidate.matches.map(({ field, column }) => [field, column]));
}
